import html2canvas from "html2canvas";
import {
  calculateLogoCenter,
  QR_CONFIG,
} from "../../constants/qrCodeConstants";

interface DownloadOptions {
  fileName: string;
  format: "png" | "svg";
  qrCodeElementId: string;
  logoUrl?: string;
  withLogo?: boolean;
  includeDisclaimer?: boolean;
  shortfyLogoUrl?: string;
}

const loadImageAsDataUrl = (src: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const attempt = (useCors: boolean) => {
      const img = new Image();
      if (useCors) img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Could not get canvas context"));

        ctx.drawImage(img, 0, 0);
        try {
          resolve(canvas.toDataURL("image/png"));
        } catch {
          if (!useCors) {
            attempt(true);
          } else {
            reject(new Error("Tainted canvas"));
          }
        }
      };

      img.onerror = () => {
        if (!useCors) {
          attempt(true);
        } else {
          reject(new Error(`Failed to load image: ${src}`));
        }
      };

      img.src = src;
    };

    attempt(false);
  });
};

const prepareElementForCapture = async (
  element: HTMLElement,
  logoUrl?: string,
  includeDisclaimer = true,
  shortfyLogoUrl?: string,
): Promise<{ cleanup: () => void }> => {
  const originalStyle = element.style.cssText;
  const logoImg = element.querySelector(".logo-img") as HTMLImageElement | null;
  const logoBg = element.querySelector(".logo-bg") as HTMLElement | null;
  const originalLogoStyle = logoImg?.style.cssText;
  const originalLogoSrc = logoImg?.src;
  const originalBgStyle = logoBg?.style.cssText;

  let disclaimerDiv: HTMLDivElement | null = null;

  if (includeDisclaimer && shortfyLogoUrl) {
    disclaimerDiv = document.createElement("div");
    disclaimerDiv.style.cssText = `
      margin-top: ${QR_CONFIG.DOWNLOAD.DISCLAIMER.MARGIN_TOP_PX}px;
      color: ${QR_CONFIG.DOWNLOAD.DISCLAIMER.COLOR};
      font-family: ${QR_CONFIG.DOWNLOAD.DISCLAIMER.FONT_FAMILY};
      font-size: ${QR_CONFIG.DOWNLOAD.DISCLAIMER.FONT_SIZE_PX}px;
      line-height: 1.2;
      text-align: center;
      max-width: ${QR_CONFIG.DISPLAY_SIZE}px;
      margin-left: auto;
      margin-right: auto;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    `;

    const textSpan = document.createElement("span");
    textSpan.textContent = "Powered by";
    disclaimerDiv.appendChild(textSpan);

    try {
      const dataUrl = await loadImageAsDataUrl(shortfyLogoUrl);
      const shortfyImg = document.createElement("img");
      shortfyImg.src = dataUrl;
      shortfyImg.alt = "Shortfy";
      shortfyImg.style.cssText =
        "height: 14px; width: auto; object-fit: contain; display: inline-block;";
      disclaimerDiv.appendChild(shortfyImg);
    } catch {
      disclaimerDiv = null;
    }

    if (disclaimerDiv) {
      element.appendChild(disclaimerDiv);
    }
  }

  element.style.padding = "30px";
  element.style.backgroundColor = QR_CONFIG.LOGO.BACKGROUND_COLOR;
  element.style.backgroundImage = "none";
  element.style.position = "relative";
  element.style.imageRendering = "pixelated";
  element.style.display = "inline-block";
  element.style.textAlign = "center";

  if (logoUrl && logoImg) {
    const logoDataUrl = await loadImageAsDataUrl(logoUrl);
    logoImg.src = logoDataUrl;
    logoImg.style.position = "absolute";
    logoImg.style.top = "50%";
    logoImg.style.left = "50%";
    logoImg.style.transform = "translate(-50%, -50%)";
    logoImg.style.zIndex = "10";
    logoImg.style.maxWidth = `${QR_CONFIG.LOGO.SIZE}px`;
    logoImg.style.maxHeight = `${QR_CONFIG.LOGO.SIZE}px`;
    logoImg.style.width = "auto";
    logoImg.style.height = "auto";
    logoImg.style.objectFit = "contain";
  }

  if (logoUrl && logoBg) {
    logoBg.style.position = "absolute";
    logoBg.style.top = "50%";
    logoBg.style.left = "50%";
    logoBg.style.transform = "translate(-50%, -50%)";
    logoBg.style.zIndex = "9";
    logoBg.style.width = `${QR_CONFIG.LOGO.TOTAL_SIZE}px`;
    logoBg.style.height = `${QR_CONFIG.LOGO.TOTAL_SIZE}px`;
    logoBg.style.display = "flex";
    logoBg.style.alignItems = "center";
    logoBg.style.justifyContent = "center";
  }

  const cleanup = () => {
    element.style.cssText = originalStyle;
    if (logoImg && originalLogoStyle !== undefined) {
      logoImg.style.cssText = originalLogoStyle;
      if (originalLogoSrc) logoImg.src = originalLogoSrc;
    }
    if (logoBg && originalBgStyle !== undefined) {
      logoBg.style.cssText = originalBgStyle;
    }
    if (disclaimerDiv?.parentNode) {
      disclaimerDiv.parentNode.removeChild(disclaimerDiv);
    }
  };

  return { cleanup };
};

const downloadAsPNG = async (options: DownloadOptions): Promise<void> => {
  const element = document.getElementById(options.qrCodeElementId);
  if (!element) throw new Error("QR Code element not found");

  const { cleanup } = await prepareElementForCapture(
    element,
    options.withLogo ? options.logoUrl : undefined,
    options.includeDisclaimer ?? true,
    options.shortfyLogoUrl,
  );

  try {
    await new Promise((r) => setTimeout(r, 300));

    const canvas = await html2canvas(element, {
      scale: QR_CONFIG.DOWNLOAD.PNG.SCALE_FACTOR,
      backgroundColor: QR_CONFIG.LOGO.BACKGROUND_COLOR,
      useCORS: true,
      allowTaint: true,
      logging: false,
    });

    const ctx = canvas.getContext("2d");
    if (ctx) ctx.imageSmoothingEnabled = QR_CONFIG.DOWNLOAD.PNG.IMAGE_SMOOTHING;

    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png", QR_CONFIG.DOWNLOAD.PNG.QUALITY);
    link.download = `${options.fileName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    cleanup();
  }
};

const downloadAsSVG = async (options: DownloadOptions): Promise<void> => {
  const element = document.getElementById(options.qrCodeElementId);
  if (!element) throw new Error("QR Code element not found");

  const qrSvg = element.querySelector("svg");
  if (!qrSvg) throw new Error("SVG element not found");

  const svgDoc = new DOMParser().parseFromString(
    new XMLSerializer().serializeToString(qrSvg),
    "image/svg+xml",
  );
  const svg = svgDoc.documentElement;

  const width = parseInt(svg.getAttribute("width") || "200", 10);
  const height = parseInt(svg.getAttribute("height") || "200", 10);
  const viewBox = svg.getAttribute("viewBox")?.split(" ").map(Number) ?? [
    0,
    0,
    width,
    height,
  ];
  const vbWidth = viewBox[2];
  const vbHeight = viewBox[3];

  if (options.withLogo && options.logoUrl) {
    try {
      const logoDataUrl = await loadImageAsDataUrl(options.logoUrl);
      const containerSize = vbWidth * QR_CONFIG.DOWNLOAD.SVG.LOGO_SIZE_RATIO;
      const { x, y } = calculateLogoCenter(vbWidth, containerSize);

      const bgRect = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "rect",
      );
      bgRect.setAttribute("x", String(x));
      bgRect.setAttribute("y", String(y));
      bgRect.setAttribute("width", String(containerSize));
      bgRect.setAttribute("height", String(containerSize));
      bgRect.setAttribute("fill", QR_CONFIG.LOGO.BACKGROUND_COLOR);
      svg.appendChild(bgRect);

      const logoSizeRatio = QR_CONFIG.LOGO.SIZE / QR_CONFIG.LOGO.TOTAL_SIZE;
      const logoSize = containerSize * logoSizeRatio;
      const logoPadding = (containerSize - logoSize) / 2;

      const imgEl = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "image",
      );
      imgEl.setAttribute("x", String(x + logoPadding));
      imgEl.setAttribute("y", String(y + logoPadding));
      imgEl.setAttribute("width", String(logoSize));
      imgEl.setAttribute("height", String(logoSize));
      imgEl.setAttribute("href", logoDataUrl);
      imgEl.setAttribute("preserveAspectRatio", "xMidYMid meet");
      svg.appendChild(imgEl);
    } catch {}
  }

  if ((options.includeDisclaimer ?? true) && options.shortfyLogoUrl) {
    try {
      const shortfyDataUrl = await loadImageAsDataUrl(options.shortfyLogoUrl);

      const extraHeight = vbHeight * 0.2;
      svg.setAttribute("viewBox", `0 0 ${vbWidth} ${vbHeight + extraHeight}`);

      const footerG = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "g",
      );
      const cy = vbHeight + extraHeight / 2;

      const textEl = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "text",
      );
      textEl.textContent = "Powered by";
      textEl.setAttribute("fill", QR_CONFIG.DOWNLOAD.DISCLAIMER.COLOR);
      textEl.setAttribute(
        "font-family",
        QR_CONFIG.DOWNLOAD.DISCLAIMER.FONT_FAMILY,
      );
      textEl.setAttribute("font-size", "0.8");
      textEl.setAttribute("dominant-baseline", "middle");
      textEl.setAttribute("text-anchor", "end");
      textEl.setAttribute("x", String(vbWidth / 2 - 0.3));
      textEl.setAttribute("y", String(cy));
      footerG.appendChild(textEl);

      const logoH = vbHeight * 0.06;
      const logoW = logoH * 3.2;
      const imgEl = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "image",
      );
      imgEl.setAttribute("x", String(vbWidth / 2 + 0.3));
      imgEl.setAttribute("y", String(cy - logoH / 2));
      imgEl.setAttribute("width", String(logoW));
      imgEl.setAttribute("height", String(logoH));
      imgEl.setAttribute("href", shortfyDataUrl);
      imgEl.setAttribute("preserveAspectRatio", "xMidYMid meet");
      footerG.appendChild(imgEl);

      svg.appendChild(footerG);
    } catch {}
  }

  const blob = new Blob([new XMLSerializer().serializeToString(svg)], {
    type: "image/svg+xml",
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `${options.fileName}.svg`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadQRCode = async (
  options: DownloadOptions,
): Promise<void> => {
  const sanitizedFileName = options.fileName
    .replace(/\s+/g, "")
    .replace(/[/:*?"<>|]/g, "_");

  const sanitizedOptions = { ...options, fileName: sanitizedFileName };

  if (options.format === "png") {
    await downloadAsPNG(sanitizedOptions);
  } else if (options.format === "svg") {
    await downloadAsSVG(sanitizedOptions);
  } else {
    throw new Error(`Unsupported format: ${options.format}`);
  }
};
